/**
 * Cart state machine — built on top of the Storefront client.
 *
 * - Persists the cart id in `localStorage` (`lmdr_cart_id`).
 * - Exposes `loadCart`, `addToCart`, `updateLine`, `adjustLineQuantity`,
 *   `removeLine`, `clearCart`, `applyCouponCode`, `reloadCart`, `getCartID`,
 *   and `whenCartIdle` for checkout synchronization.
 * - Dispatches `smootify:*` events on `document` per PRD §8.3.2:
 *     - `smootify:cart_updated`   (detail = cart)
 *     - `smootify:added_to_cart`  (detail = { lines, cart, goToCheckout })
 *     - `smootify:removed_from_cart` (detail = lineIds[])
 * - Serializes all mutating operations through an async mutex to avoid
 *   race conditions (e.g. two rapid add-to-cart clicks).
 */

import { storefront } from "../storefront/client.js";
import {
  CART_QUERY,
  CART_CREATE,
  CART_LINES_ADD,
  CART_LINES_UPDATE,
  CART_LINES_REMOVE,
  CART_DISCOUNT_CODES_UPDATE,
  CART_BUYER_IDENTITY_UPDATE,
  CART_NOTE_UPDATE,
} from "../storefront/queries.js";
import { getCartId, setCartId, clearCartId } from "./storage.js";
import { getCurrentCountry } from "../i18n/locale.js";
import type {
  Cart,
  CartLineInput,
  CartLineUpdateInput,
  CartUserError,
} from "../types.js";

// ---------------------------------------------------------------------------
//  Mutex
// ---------------------------------------------------------------------------

let chain: Promise<unknown> = Promise.resolve();

function runExclusive<T>(task: () => Promise<T>): Promise<T> {
  const guardedTask = async (): Promise<T> => {
    try {
      return await task();
    } catch (error) {
      // A transport failure does not prove that Shopify rejected a mutation.
      // Refresh before the next relative edit or checkout uses this snapshot.
      cartNeedsRefresh = true;
      throw error;
    }
  };
  const next = chain.then(guardedTask, guardedTask);
  // Swallow errors on the chain so one failure doesn't poison later calls.
  chain = next.catch(() => undefined);
  return next;
}

// ---------------------------------------------------------------------------
//  In-memory snapshot
// ---------------------------------------------------------------------------

let currentCart: Cart | null = null;
let cartNeedsRefresh = false;

// Storage can be unavailable in privacy modes. Keep that session's cart usable.
function activeCartId(): string | null {
  return getCartId() ?? currentCart?.id ?? null;
}

function assertQuantity(quantity: number, minimum = 0): void {
  if (!Number.isSafeInteger(quantity) || quantity < minimum) {
    throw new Error(`Cart quantity must be an integer greater than or equal to ${minimum}`);
  }
}

async function snapshotFor(cartId: string): Promise<Cart> {
  if (currentCart?.id === cartId && !cartNeedsRefresh) return currentCart;
  const cart = await fetchCart(cartId);
  if (!cart) throw new Error("Cart is no longer available; please reload the page");
  currentCart = cart;
  cartNeedsRefresh = false;
  return cart;
}

// ---------------------------------------------------------------------------
//  Event helpers
// ---------------------------------------------------------------------------

function dispatch(name: string, detail: unknown): void {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

function emitUpdated(cart: Cart | null): void {
  currentCart = cart;
  cartNeedsRefresh = false;
  dispatch("smootify:cart_updated", cart);
}

// ---------------------------------------------------------------------------
//  Response shape helpers
// ---------------------------------------------------------------------------

interface CartMutationResult {
  cart: Cart | null;
  userErrors: CartUserError[] | null;
}

function assertMutationOk(
  label: string,
  result: CartMutationResult | undefined,
): Cart {
  if (!result) {
    throw new Error(`${label}: empty response`);
  }
  if (result.userErrors && result.userErrors.length > 0) {
    const msg = result.userErrors.map((e) => e.message).join("; ");
    throw new Error(`${label}: ${msg}`);
  }
  if (!result.cart) {
    throw new Error(`${label}: no cart returned`);
  }
  return result.cart;
}

// ---------------------------------------------------------------------------
//  Storefront calls (all with cache disabled for mutations / live reads)
// ---------------------------------------------------------------------------

async function fetchCart(cartId: string): Promise<Cart | null> {
  const data = await storefront.query<{ cart: Cart | null }>(
    CART_QUERY,
    { cartId },
    { cache: false },
  );
  // Only an explicit null establishes expiration. A malformed response or a
  // transport failure must never invalidate the persisted cart identifier.
  if (data.cart === undefined) throw new Error("cart: empty response");
  return data.cart;
}

async function createCart(lines: CartLineInput[]): Promise<Cart> {
  const data = await storefront.query<{ cartCreate: CartMutationResult }>(
    CART_CREATE,
    {
      input: {
        lines: lines.map((l) => ({
          merchandiseId: l.merchandiseId,
          quantity: l.quantity,
          attributes: l.attributes ?? [],
        })),
        buyerIdentity: { countryCode: getCurrentCountry() },
        attributes: [],
      },
    },
    { cache: false },
  );
  return assertMutationOk("cartCreate", data.cartCreate);
}

async function linesAdd(cartId: string, lines: CartLineInput[]): Promise<Cart> {
  const data = await storefront.query<{ cartLinesAdd: CartMutationResult }>(
    CART_LINES_ADD,
    {
      cartId,
      lines: lines.map((l) => ({
        merchandiseId: l.merchandiseId,
        quantity: l.quantity,
        attributes: l.attributes ?? [],
      })),
    },
    { cache: false },
  );
  return assertMutationOk("cartLinesAdd", data.cartLinesAdd);
}

async function linesUpdate(
  cartId: string,
  lines: CartLineUpdateInput[],
): Promise<Cart> {
  const data = await storefront.query<{ cartLinesUpdate: CartMutationResult }>(
    CART_LINES_UPDATE,
    {
      cartId,
      lines: lines.map((l) => ({
        id: l.id,
        quantity: l.quantity,
        ...(l.attributes !== undefined ? { attributes: l.attributes } : {}),
      })),
    },
    { cache: false },
  );
  return assertMutationOk("cartLinesUpdate", data.cartLinesUpdate);
}

async function linesRemove(cartId: string, lineIds: string[]): Promise<Cart> {
  const data = await storefront.query<{ cartLinesRemove: CartMutationResult }>(
    CART_LINES_REMOVE,
    { cartId, lineIds },
    { cache: false },
  );
  return assertMutationOk("cartLinesRemove", data.cartLinesRemove);
}

async function discountCodesUpdate(
  cartId: string,
  codes: string[],
): Promise<Cart> {
  const data = await storefront.query<{
    cartDiscountCodesUpdate: CartMutationResult;
  }>(
    CART_DISCOUNT_CODES_UPDATE,
    { cartId, discountCodes: codes },
    { cache: false },
  );
  return assertMutationOk(
    "cartDiscountCodesUpdate",
    data.cartDiscountCodesUpdate,
  );
}

async function buyerIdentityUpdate(
  cartId: string,
  countryCode: string,
): Promise<Cart> {
  const data = await storefront.query<{
    cartBuyerIdentityUpdate: CartMutationResult;
  }>(
    CART_BUYER_IDENTITY_UPDATE,
    { cartId, buyerIdentity: { countryCode } },
    { cache: false },
  );
  return assertMutationOk(
    "cartBuyerIdentityUpdate",
    data.cartBuyerIdentityUpdate,
  );
}

// ---------------------------------------------------------------------------
//  Public API
// ---------------------------------------------------------------------------

export async function loadCart(): Promise<Cart | null> {
  return runExclusive(async () => {
    const id = activeCartId();
    if (!id) {
      emitUpdated(null);
      return null;
    }
    const cart = await fetchCart(id);
    if (!cart) clearCartId();
    emitUpdated(cart);
    return cart;
  });
}

export async function addToCart(
  lines: CartLineInput[],
  goToCheckout = false,
): Promise<Cart> {
  return runExclusive(async () => {
    if (lines.length === 0) {
      throw new Error("addToCart: at least one line required");
    }
    for (const line of lines) assertQuantity(line.quantity, 1);

    let cart: Cart;
    const existingId = activeCartId();
    if (!existingId) {
      cart = await createCart(lines);
      setCartId(cart.id);
    } else {
      try {
        cart = await linesAdd(existingId, lines);
      } catch (error) {
        // An add may have reached Shopify even when its response is lost.
        // Never replace that cart or retry the add on an ambiguous failure.
        // Only a successful read returning null proves that it has expired.
        let existing: Cart | null;
        try {
          existing = await fetchCart(existingId);
        } catch {
          throw error;
        }
        if (existing) {
          emitUpdated(existing);
          throw error;
        }
        cart = await createCart(lines);
        setCartId(cart.id);
      }
    }

    emitUpdated(cart);
    dispatch("smootify:added_to_cart", { lines, cart, goToCheckout });

    if (goToCheckout && typeof window !== "undefined" && cart.checkoutUrl) {
      window.location.href = cart.checkoutUrl;
    }

    return cart;
  });
}

export async function updateLine(
  lineId: string,
  quantity: number,
  attrs?: CartLineUpdateInput["attributes"],
): Promise<Cart> {
  return runExclusive(async () => {
    assertQuantity(quantity);
    const id = activeCartId();
    if (!id) throw new Error("updateLine: no cart");
    const snapshot = await snapshotFor(id);
    if (!snapshot.lines.some((line) => line.id === lineId)) return snapshot;
    if (quantity === 0) return removeLineFromSnapshot(id, lineId, snapshot);
    const cart = await linesUpdate(id, [
      attrs !== undefined
        ? { id: lineId, quantity, attributes: attrs }
        : { id: lineId, quantity },
    ]);
    emitUpdated(cart);
    return cart;
  });
}

/** Apply each click to the quantity after all preceding mutations completed. */
export async function adjustLineQuantity(
  lineId: string,
  delta: number,
): Promise<Cart> {
  return runExclusive(async () => {
    if (!Number.isSafeInteger(delta)) throw new Error("Cart quantity delta must be an integer");
    const id = activeCartId();
    if (!id) throw new Error("adjustLineQuantity: no cart");
    const snapshot = await snapshotFor(id);
    const line = snapshot.lines.find((item) => item.id === lineId);
    // A second queued remove/decrement can outlive the line's DOM element.
    if (!line || delta === 0) return snapshot;
    const quantity = Math.max(0, line.quantity + delta);
    assertQuantity(quantity);
    if (quantity === 0) return removeLineFromSnapshot(id, lineId, snapshot);
    const cart = await linesUpdate(id, [{ id: lineId, quantity }]);
    emitUpdated(cart);
    return cart;
  });
}

async function removeLineFromSnapshot(
  cartId: string,
  lineId: string,
  snapshot: Cart,
): Promise<Cart> {
  if (!snapshot.lines.some((line) => line.id === lineId)) return snapshot;
  const cart = await linesRemove(cartId, [lineId]);
  emitUpdated(cart);
  dispatch("smootify:removed_from_cart", [lineId]);
  return cart;
}

export async function removeLine(lineId: string): Promise<Cart> {
  return runExclusive(async () => {
    const id = activeCartId();
    if (!id) throw new Error("removeLine: no cart");
    return removeLineFromSnapshot(id, lineId, await snapshotFor(id));
  });
}

export async function clearCart(): Promise<Cart | null> {
  return runExclusive(async () => {
    const id = activeCartId();
    if (!id) {
      emitUpdated(null);
      return null;
    }
    const snapshot = currentCart?.id === id && !cartNeedsRefresh
      ? currentCart
      : await fetchCart(id);
    if (!snapshot || snapshot.lines.length === 0) {
      if (!snapshot) clearCartId();
      emitUpdated(snapshot);
      return snapshot;
    }
    const lineIds = snapshot.lines.map((l) => l.id);
    const cart = await linesRemove(id, lineIds);
    emitUpdated(cart);
    dispatch("smootify:removed_from_cart", lineIds);
    return cart;
  });
}

export async function updateCartNote(note: string): Promise<Cart | null> {
  return runExclusive(async () => {
    const id = activeCartId();
    if (!id) {
      // Pas de panier encore → on ignore la note plutôt que de créer un panier
      // vide. La note sera re-posée lors du 1er add-to-cart.
      return currentCart;
    }
    const data = await storefront.query<{
      cartNoteUpdate: CartMutationResult;
    }>(
      CART_NOTE_UPDATE,
      { cartId: id, note },
      { cache: false },
    );
    const cart = assertMutationOk("cartNoteUpdate", data.cartNoteUpdate);
    emitUpdated(cart);
    return cart;
  });
}

export class InvalidCouponError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(`Coupon "${code}" rejected by Shopify`);
    this.name = "InvalidCouponError";
    this.code = code;
  }
}

export async function applyCouponCode(code: string): Promise<Cart> {
  return runExclusive(async () => {
    const id = activeCartId();
    if (!id) throw new Error("applyCouponCode: no cart");
    const trimmed = code.trim();
    const codes = trimmed.length > 0 ? [trimmed] : [];
    const cart = await discountCodesUpdate(id, codes);
    emitUpdated(cart);
    // Détection d'un code rejeté par Shopify : le cart contient bien le code
    // qu'on vient d'envoyer, mais avec `applicable: false`. Shopify ne throw
    // pas d'userError pour ce cas — il accepte l'écriture mais marque le
    // code inapplicable (expiré, non existant, non éligible).
    if (trimmed.length > 0) {
      const codeEntry = (cart.discountCodes ?? []).find(
        (c) => c.code.toLowerCase() === trimmed.toLowerCase(),
      );
      if (codeEntry && !codeEntry.applicable) {
        throw new InvalidCouponError(trimmed);
      }
    }
    return cart;
  });
}

export async function reloadCart(
  opts: { syncBuyerIdentity?: boolean } = {},
): Promise<Cart | null> {
  return runExclusive(async () => {
    const id = activeCartId();
    if (!id) {
      emitUpdated(null);
      return null;
    }
    let cart: Cart | null;
    if (opts.syncBuyerIdentity) {
      try {
        cart = await buyerIdentityUpdate(id, getCurrentCountry());
      } catch {
        cart = await fetchCart(id);
      }
    } else {
      cart = await fetchCart(id);
    }
    if (!cart) clearCartId();
    emitUpdated(cart);
    return cart;
  });
}

export async function getCartID(): Promise<string> {
  const existing = activeCartId();
  if (existing) return existing;

  // Create an empty cart on demand. This mirrors Smootify behavior where the
  // id is fetched lazily by integrations (analytics, checkout widgets, …).
  return runExclusive(async () => {
    const again = activeCartId();
    if (again) return again;
    const cart = await storefront.query<{ cartCreate: CartMutationResult }>(
      CART_CREATE,
      {
        input: {
          lines: [],
          buyerIdentity: { countryCode: getCurrentCountry() },
          attributes: [],
        },
      },
      { cache: false },
    );
    const created = assertMutationOk("cartCreate", cart.cartCreate);
    setCartId(created.id);
    emitUpdated(created);
    return created.id;
  });
}

export function getCartSnapshot(): Cart | null {
  return currentCart;
}

export function getCurrentCart(): Cart | null {
  return currentCart;
}

/** Resolve with the latest snapshot once all queued mutations have settled. */
export async function whenCartIdle(): Promise<Cart | null> {
  let pending: Promise<unknown>;
  do {
    pending = chain;
    await pending;
  } while (pending !== chain);
  if (cartNeedsRefresh) {
    await loadCart();
    return whenCartIdle();
  }
  return currentCart;
}

export type { CartLineInput, CartLineUpdateInput };
